import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import Config from './config';
import { MeProviderDataFragment } from '../generated/graphql';

const safeSentryCall = (callback: () => void) => {
  try {
    if (Config.REACT_APP_SENTRY_DSN) {
      callback();
    } else {
      console.warn('[Sentry] DSN is not set, skipping Sentry operation');
    }
  } catch (error) {
    // Log with more context for debugging different failure types
    console.error('[Sentry] Operation failed:', {
      error,
      errorMessage: error instanceof Error ? error.message : String(error),
      errorStack: error instanceof Error ? error.stack : undefined,
      errorType: error?.constructor?.name,
    });

    // Re-throw critical failures that indicate bugs in how we're calling Sentry APIs
    // These shouldn't be suppressed as they represent real code issues.
    if (error instanceof TypeError || error instanceof ReferenceError) {
      throw error;
    }
  }
};

/**
 * Errors that are expected / environmental and have no actionable code fix. We
 * drop them in `ignoreErrors` so they don't create Sentry noise or false
 * alerts.
 *
 * - "Token is expired" (WEB-AB): an Auth0/session token expiring during a
 *   background GraphQL operation. The Apollo errorLink already handles
 *   `TokenInvalid` by clearing the session and redirecting to login; a raw
 *   expiry surfacing as an unhandled rejection is the same condition and is
 *   not a bug.
 * - "WKWebView API client did not respond to this postMessage" (WEB-A7): a
 *   native iOS WKWebView bridge failure from the in-app browser. There is no
 *   web code path to fix — the native bridge simply stops responding.
 * - "window.webkit.messageHandlers" (WEB-AP): a TypeError thrown when an iOS
 *   in-app browser (e.g. Instagram's) probes `window.webkit.messageHandlers`
 *   and the property is undefined. The failing frame is the page's own inline
 *   HTML bootstrap, not app source — there is no web code path to fix; the
 *   native bridge is simply absent outside a real WKWebView.
 * - "No bearer token" (WEB-G): a GraphQL request reaching the backend without
 *   an `Authorization: Bearer` header. Every recorded event has the page url
 *   set to `/login` regardless of the route (transaction), which means
 *   in-flight Apollo queries fire while a session is being cleared / the user
 *   is being redirected to login after token expiry. The Apollo errorLink already
 *   handles `TokenInvalid` by clearing the session and redirecting to login.
 * - WEB-H: "Load failed" — Safari's generic network failure, surfaced as an
 *   Apollo networkError from transient causes
 * - WEB-N / WEB-G: "Activity already completed" / "Activity has been voided" —
 *   backend business-logic guards raised by `completeProgramActivity` when the
 *   activity is already in a terminal state (completed or voided) by the time
 *   the request lands. They stem from a race (double-click, a stale slideover,
 *   a concurrent action, or a server-side void) rather than a bug. The
 *   completion handlers in `PatientFollowUpDetails`, `PatientHome`, and
 *   `PatientLibrary` guard against double-submission, recognize both guards via
 *   `isProgramActivityAlreadyActionedError`, and surface a user-friendly toast,
 *   so there is nothing further to action in code. These entries are a
 *   defense-in-depth backstop for any capture path the handlers don't cover.
 * - WEB-9M: "Failed to fetch" — Chrome's generic network failure (the
 *   cross-browser counterpart to Safari's "Load failed"). It surfaces as an
 *   unhandled Apollo rejection when an in-flight query is aborted by
 *   navigation to `/login` (session-expiry redirect race). The retryLink
 *   already treats this exact message as a retryable network condition, so it
 *   is environmental/transient with no actionable web code path.
 * - WEB-9M (Firefox variant): "NetworkError when attempting to fetch resource."
 *   — Firefox's equivalent of Chrome's "Failed to fetch". Same environmental
 *   condition (in-flight query aborted by navigation / session-expiry redirect
 *   race). The retryLink has been updated to match this variant too.
 * - WEB-5S: "Invalid call to runtime.sendMessage(). Tab not found." — an
 *   unhandled rejection from a browser extension's content script
 *   (WebExtension MV3 messaging API). No stacktrace is available (the throwing
 *   code is not in our bundle). It cannot be fixed from web source.
 * - WEB-AS: "Converting circular structure to JSON ... property '__reactFiber"
 *   — a TypeError thrown when an injected page script (browser extension)
 *   calls JSON.stringify over a DOM subtree that contains an element owned by
 *   React. The serializer follows the element's `__reactFiber$...` prop into
 *   the fiber tree and back via `stateNode`, forming a cycle. The stack is
 *   entirely anonymous frames + react-dom internals (no first-party frame),
 *   so there is no app call site to fix. The regex is anchored on the
 *   React-fiber property name so a generic circular-JSON failure in app code
 *   (which would not name `__reactFiber`) is NOT suppressed.
 * - WEB-AT: "Cannot access '__WEBPACK_DEFAULT_EXPORT__' before initialization"
 *   — a ReferenceError thrown by Webpack's HMR / React Fast Refresh runtime
 *   (the stack runs through `performReactRefresh` / `scheduleRoot`). Every
 *   recorded event is `environment: development` at `localhost:3000`, sampled
 *   at rate 1. It is a dev-only hot-reload artifact (a transient TDZ state as
 *   a module is swapped) that never occurs in production builds, which ship no
 *   HMR runtime. There is no production code path to fix; the regex is anchored
 *   on the Webpack internal symbol so a genuine TDZ bug in app source (which
 *   would not name `__WEBPACK_DEFAULT_EXPORT__`) is NOT suppressed.
 * - WEB-B0 / WEB-B1: dev-only Webpack HMR artifact. During React Fast Refresh
 *   a stale reference to a generated GraphQL hook is invoked mid-module-swap,
 *   throwing "useXxxQuery is not a function". Every event is
 *   environment: development at localhost:3000 with a stack through
 *   performReactRefresh and a .hot-update.js frame — never in production
 *   builds (no HMR runtime). The regex is anchored on BOTH the codegen module
 *   name (_generated_graphql__WEBPACK_IMPORTED_MODULE_) AND a JavaScript
 *   identifier pattern (\.[a-zA-Z_$][a-zA-Z0-9_$]*) so a genuine missing-export
 *   bug in app source (which would not use this module pattern) is NOT
 *   suppressed. The identifier constraint ensures we don't suppress garbage
 *   or malformed text, while still matching all valid GraphQL hook names.
 *   NOTE: This pattern is environment-agnostic because the development-
 *   environment check happens at Sentry ingestion time (all matching events
 *   have environment: development).
 */
// Anchored on the exact Safari noise shapes so rejections carrying real data
// (e.g. "Object captured as promise rejection with keys: code, message", or a
// CustomEvent of another type) still reach Sentry.
const CUSTOM_EVENT_REJECTION =
  /^Event `CustomEvent` \(type=unhandledrejection\) captured as promise rejection$/;
const EMPTY_OBJECT_REJECTION =
  /^Object captured as promise rejection with keys: \[object has no keys\]$/;

const IGNORE_ERRORS = [
  'Token is expired',
  'WKWebView API client did not respond to this postMessage',
  'window.webkit.messageHandlers',
  "undefined is not an object (evaluating 'window.webkit')",
  'No bearer token',
  /^Load failed$/,
  'Activity already completed',
  'Activity has been voided',
  /^Failed to fetch$/,
  /^NetworkError when attempting to fetch resource\.$/,
  'Invalid call to runtime.sendMessage(). Tab not found',
  /Converting circular structure to JSON[\s\S]*property '__reactFiber/,
  /^Cannot access '__WEBPACK_DEFAULT_EXPORT__' before initialization$/,
  // WEB-9W: CustomEvent rejected as a promise rejection (Safari). The rejection
  // value is a synthetic CustomEvent with type=unhandledrejection, detail=null.
  // These are non-Error rejections with no stacktrace — pure noise.
  CUSTOM_EVENT_REJECTION,
  // WEB-9V: Empty object rejected as a promise rejection (Safari). The rejection
  // value is a bare object with no enumerable keys and no message/stack.
  EMPTY_OBJECT_REJECTION,
  // NOTE: intentionally NOT a generic "Non-Error promise rejection captured"
  // catch-all — that would also suppress actionable non-Error rejections from
  // dependencies. Only the two documented Safari shapes above are filtered.
  // WEB-B0 / WEB-B1: dev-only Webpack HMR artifact. During React Fast Refresh
  // a stale reference to a generated GraphQL hook is invoked mid-module-swap,
  // throwing "useXxxQuery is not a function". Every event is
  // environment: development at localhost:3000 with a stack through
  // performReactRefresh and a .hot-update.js frame — never in production
  // builds (no HMR runtime). The regex is anchored on BOTH the codegen module
  // name (_generated_graphql__WEBPACK_IMPORTED_MODULE_) AND a JavaScript
  // identifier pattern (\.[a-zA-Z_$][a-zA-Z0-9_$]*) so a genuine missing-export
  // bug in app source (which would not use this module pattern) is NOT
  // suppressed. The identifier constraint ensures we don't suppress garbage
  // or malformed text, while still matching all valid GraphQL hook names.
  // NOTE: This pattern is environment-agnostic because the development-
  // environment check happens at Sentry ingestion time (all matching events
  // have environment: development).
  /_generated_graphql__WEBPACK_IMPORTED_MODULE_\d+__\.[a-zA-Z_$][a-zA-Z0-9_$]*\) is not a function/,
];

export { IGNORE_ERRORS };

const initSentry = () => {
  if (Config.REACT_APP_SENTRY_DSN)
    safeSentryCall(() => {
      Sentry.init({
        debug: Config.REACT_APP_SENTRY_DEBUG,
        dsn: Config.REACT_APP_SENTRY_DSN,
        integrations: [
          Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
          }),
        ],
        environment: Config.REACT_APP_ENVIRONMENT,
        release: Config.REACT_APP_VERSION,
        ignoreErrors: IGNORE_ERRORS,
        beforeSend(event) {
          // Drop non-Error unhandled rejections that slip through ignoreErrors.
          // Sentry's GlobalHandlers uses window.onunhandledrejection (a property
          // handler), so our addEventListener-based suppressNonErrorRejections
          // cannot stop it via preventDefault(). This beforeSend acts as the
          // final safety net.
          //
          // Scope: ONLY the two known Safari noise shapes are dropped, so that
          // actionable non-Error rejections from dependencies are NOT silently
          // discarded:
          // - WEB-9W: "Event `CustomEvent` ... captured as promise rejection"
          // - WEB-9V: "Object captured as promise rejection with keys ..."
          const mech = event.exception?.values?.[0]?.mechanism;
          if (
            mech?.type === 'onunhandledrejection' &&
            mech?.handled === false
          ) {
            const value = event.exception?.values?.[0]?.value ?? '';
            if (
              CUSTOM_EVENT_REJECTION.test(value) ||
              EMPTY_OBJECT_REJECTION.test(value)
            ) {
              return null;
            }
          }
          return event;
        },
        tracesSampleRate: parseFloat(Config.REACT_APP_SENTRY_SAMPLE_RATE),
      });
    });
};

const setUserScope = (meProvider: MeProviderDataFragment) => {
  safeSentryCall(() => {
    Sentry.setTag('providerUserId', meProvider.id);
    Sentry.setTag('providerId', meProvider.provider.id);
    Sentry.setTag('role', meProvider.role);
  });
};

const clearUserScope = () => {
  safeSentryCall(() => {
    Sentry.setTag('providerUserId', null);
    Sentry.setTag('providerId', null);
    Sentry.setTag('role', null);
  });
};

const SentryHelpers = {
  initSentry,
  setUserScope,
  clearUserScope,
  safeSentryCall,
};

export default SentryHelpers;
